// The long-term side: a paper portfolio that follows MoneyHQ's tested momentum basket (the weekly top
// 20, equal weight, kept while in the top 40), replayed by the API on real prices, against an
// equal-weight basket of the same stocks. Started from today, or from a past date (a replay).
import { useCallback, useEffect, useRef, useState } from 'react';
import { ColorType, LineSeries, createChart, type UTCTimestamp } from 'lightweight-charts';
import { toast } from 'sonner';
import { Link } from '@/lib/nav';
import { api } from '@/lib/api';
import { useFeatures } from '@/lib/client/use-market';
import { palette } from '@/charts/palette';
import { useResolvedTheme } from '@/lib/client/use-me';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { openDesk } from './desk';

interface Holding { symbol: string; qty: number; since: string; avgPrice: number; lastClose: number; value: number; gainPct: number; rankNow?: number }
interface PfTrade { date: string; symbol: string; side: 'buy' | 'sell'; price: number; value: number; reason: string }
interface PfState { status: 'waiting' | 'running'; value: number; cash: number; returnPct: number; benchmarkPct: number; costs: number; holdings: Holding[]; recentTrades: PfTrade[]; tradeCount: number; curve: { date: string; value: number; benchmark: number }[]; lastDate: string }
interface PfRow { id: string; capital: number; startDate: string; replay: boolean; state: PfState }

const inr = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;
const pct = (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(1)}%`;
const day = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

function Curve({ curve }: { curve: PfState['curve'] }) {
  const el = useRef<HTMLDivElement>(null);
  const theme = useResolvedTheme();
  useEffect(() => {
    if (!el.current || curve.length < 2) return;
    const C = palette();
    const chart = createChart(el.current, {
      autoSize: true, layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: C.mute, fontSize: 11, attributionLogo: false },
      grid: { vertLines: { visible: false }, horzLines: { color: C.gridSoft } }, rightPriceScale: { borderVisible: false }, timeScale: { borderVisible: false },
      handleScroll: false, handleScale: false,
      localization: { priceFormatter: (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}` },
    });
    const t = (d: string) => (Date.parse(`${d}T00:00:00Z`) / 1000) as UTCTimestamp;
    chart.addSeries(LineSeries, { color: C.blue, lineWidth: 2, title: 'Momentum basket', priceLineVisible: false }).setData(curve.map((p) => ({ time: t(p.date), value: p.value })));
    chart.addSeries(LineSeries, { color: C.mute2, lineWidth: 1, title: 'Equal-weight basket', priceLineVisible: false }).setData(curve.map((p) => ({ time: t(p.date), value: p.benchmark })));
    chart.timeScale().fitContent();
    return () => chart.remove();
  }, [curve, theme]);
  return <div ref={el} className="h-56 w-full" />;
}

/** Shown only while its release switch is on for the user. */
export function MomentumPortfolio() {
  const feat = useFeatures();
  return feat.on('momentum_portfolio') ? <MomentumPortfolioSection /> : null;
}

function MomentumPortfolioSection() {
  const [rows, setRows] = useState<PfRow[] | null>(null);
  const [from, setFrom] = useState('today');
  const [custom, setCustom] = useState('2026-01-01');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api('/v1/portfolio').then((r) => (r.ok ? r.json() : [])).then(setRows).catch(() => setRows([])), []);
  useEffect(() => { void load(); }, [load]);
  const capital = (() => { try { return Number((localStorage.getItem('mhq.capital') ?? '').replace(/[^\d.]/g, '')) || 100000; } catch { return 100000; } })();

  const start = async () => {
    setBusy(true);
    const date = from === 'today' ? '' : from === 'custom' ? custom : from;
    const r = await api('/v1/portfolio', { method: 'POST', body: JSON.stringify({ capital, from: date }) });
    setBusy(false);
    if (r.ok) { toast.success(date ? 'Replayed from that date' : 'Started: it buys at the next open'); void load(); }
    else toast.error((await r.json().catch(() => ({}))).error ?? 'Could not start it');
  };
  const stop = async (id: string) => {
    if (!confirm('Stop this paper portfolio?')) return;
    const r = await api(`/v1/portfolio/${id}`, { method: 'DELETE' });
    if (r.ok) void load();
  };
  if (rows === null) return null;
  const p = rows[0];

  return (
    <Card className="mt-6 gap-0 py-0">
      <CardHeader className="border-b py-4">
        <CardTitle className="text-[15px] font-semibold">Long-term portfolio · momentum basket</CardTitle>
        <p className="mt-0.5 text-xs text-muted-foreground">
          MoneyHQ’s tested long-term rule on paper: each week, the 20 strongest NSE 500 stocks by momentum, equal weight, kept while they stay in the top 40, changed at the next open. In MoneyHQ’s test (2019–2026, after costs) it beat an equal-weight basket of the same stocks by about 8 points a year; results on today’s index list are optimistic.
        </p>
      </CardHeader>
      <CardContent className="py-4 text-[13px]">
        {!p ? (
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">Start
              <select value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 rounded-md border bg-card px-2 text-[13px] text-ink">
                <option value="today">From today (buys at the next open)</option>
                <option value="2026-07-01">Replay from 1 Jul 2026</option>
                <option value="2026-01-01">Replay from 1 Jan 2026</option>
                <option value="custom">Replay from a date…</option>
              </select>
            </label>
            {from === 'custom' && <input type="date" value={custom} onChange={(e) => setCustom(e.target.value)} className="h-8 rounded-md border bg-card px-2" aria-label="Replay from" />}
            <Button className="h-8 rounded-full px-4" disabled={busy} onClick={() => void start()}>{busy ? 'Starting…' : `Start with ${inr(capital)}`}</Button>
            <span className="text-xs text-muted-foreground">Your capital from the trade plan’s position-size box. A replay shows what the rule would have done; not a forecast.</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
              <div><div className="text-xs text-muted-foreground">Value</div><div className="num text-xl font-semibold">{inr(p.state.value)}</div></div>
              <div><div className="text-xs text-muted-foreground">Return</div><div className={cn('num text-xl font-semibold', p.state.returnPct >= 0 ? 'text-bull' : 'text-bear')}>{pct(p.state.returnPct)}</div></div>
              <div><div className="text-xs text-muted-foreground">Equal-weight basket</div><div className="num text-xl font-semibold text-muted-foreground">{pct(p.state.benchmarkPct)}</div></div>
              <div className="text-xs text-muted-foreground">
                {p.replay ? 'Replay' : 'Started'} {day(p.startDate)} with {inr(p.capital)} · {p.state.tradeCount} trades · costs {inr(p.state.costs)}{p.state.lastDate && ` · to ${day(p.state.lastDate)}`}
              </div>
              <div className="ml-auto flex gap-1">
                <Button variant="ghost" size="sm" className="h-7 text-[12px] text-brand-strong" onClick={() => openDesk('How is my long-term momentum portfolio doing, and what is it teaching me?')}>Ask the desk</Button>
                <Button variant="ghost" size="sm" className="h-7 text-[12px]" onClick={() => void stop(p.id)}>Stop</Button>
              </div>
            </div>
            {p.state.status === 'waiting'
              ? <p className="text-muted-foreground">Waiting for the next session’s open to buy this week’s top 20.</p>
              : <Curve curve={p.state.curve} />}
            {p.state.holdings.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px]">
                  <thead className="text-left text-[11px] tracking-wide text-muted-foreground uppercase">
                    <tr className="border-b">{['Stock', 'Since', 'Bought', 'Last', 'Gain', 'Rank now', 'Value'].map((h) => <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {p.state.holdings.map((h) => (
                      <tr key={h.symbol} className="border-b border-border/60 last:border-b-0">
                        <td className="px-2 py-1.5 font-semibold"><Link href={`/stock/${h.symbol}`} className="hover:text-brand-strong">{h.symbol}</Link></td>
                        <td className="num px-2 py-1.5 text-muted-foreground">{day(h.since)}</td>
                        <td className="num px-2 py-1.5">{h.avgPrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                        <td className="num px-2 py-1.5">{h.lastClose.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                        <td className={cn('num px-2 py-1.5 font-medium', h.gainPct >= 0 ? 'text-bull' : 'text-bear')}>{pct(h.gainPct)}</td>
                        <td className="num px-2 py-1.5 text-muted-foreground">{h.rankNow ? `#${h.rankNow}` : '—'}</td>
                        <td className="num px-2 py-1.5">{inr(h.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {p.state.recentTrades.length > 0 && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">Recent swaps ({p.state.tradeCount} trades in all)</summary>
                <ul className="mt-2 space-y-0.5">
                  {[...p.state.recentTrades].reverse().slice(0, 12).map((t, i) => (
                    <li key={i} className="num">{day(t.date)} · <span className={t.side === 'buy' ? 'text-bull' : 'text-bear'}>{t.side}</span> {t.symbol} at {t.price.toLocaleString('en-IN', { maximumFractionDigits: 2 })} ({inr(t.value)}) · {t.reason}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
